import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { UserPlus, Calendar, Check, X, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTrainingModules } from '../hooks/useTrainingModules';
import { useTrainingAssignments } from '../hooks/useTrainingAssignments';
import { format } from 'date-fns';

export const AssignmentCreator: React.FC = () => {
  const { modules } = useTrainingModules();
  const { sellers, assignments, assignModules } = useTrainingAssignments();
  const [selectedSeller, setSelectedSeller] = useState<string>('');
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [expectedCompletion, setExpectedCompletion] = useState<string>('');

  const activeModules = modules.filter(m => m.is_active);

  // Get modules already assigned to selected seller
  const assignedModuleIds = assignments
    .filter(a => a.seller_id === selectedSeller)
    .map(a => a.module_id);

  const availableModules = activeModules.filter(m => !assignedModuleIds.includes(m.id));

  const toggleModule = (moduleId: string) => {
    setSelectedModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleAssign = async () => {
    if (!selectedSeller || selectedModules.length === 0) return;

    // Sort selected modules by their order_position
    const sortedModuleIds = selectedModules.sort((a, b) => {
      const moduleA = modules.find(m => m.id === a);
      const moduleB = modules.find(m => m.id === b);
      return (moduleA?.order_position ?? 0) - (moduleB?.order_position ?? 0);
    });

    await assignModules.mutateAsync({
      sellerId: selectedSeller,
      moduleIds: sortedModuleIds,
      expectedCompletion: expectedCompletion || null,
    });

    setSelectedSeller('');
    setSelectedModules([]);
    setExpectedCompletion('');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
          <UserPlus className="w-6 h-6 text-blue-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold text-lg">Asignar Entrenamiento</h3>
          <p className="text-blue-400/80 text-sm">Selecciona un vendedor y los módulos a asignar</p>
        </div>
      </div>

      {sellers.length === 0 ? (
        <div className="text-center py-8 bg-white/5 rounded-xl">
          <Users className="w-12 h-12 mx-auto mb-3 text-white/20" />
          <p className="text-white/50">No hay vendedores registrados</p>
          <p className="text-white/30 text-sm">Crea vendedores en el tab "Equipo"</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Seller Selection */}
          <div>
            <label className="text-white/60 text-sm mb-2 block">Vendedor</label>
            <Select value={selectedSeller} onValueChange={setSelectedSeller}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Selecciona un vendedor" className="text-white" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {sellers.map(seller => (
                  <SelectItem 
                    key={seller.user_id} 
                    value={seller.user_id}
                    className="text-white hover:bg-white/10 focus:bg-white/10 focus:text-white"
                  >
                    {seller.nombre}
                    {seller.ubicacion_tienda && (
                      <span className="text-white/50 ml-2">
                        ({seller.ubicacion_tienda})
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Module Selection */}
          {selectedSeller && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <label className="text-white/60 text-sm mb-2 block">
                Módulos a asignar ({availableModules.length} disponibles)
              </label>
              
              {availableModules.length === 0 ? (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 text-center">
                  <p className="text-yellow-400 text-sm">
                    Este vendedor ya tiene todos los módulos asignados
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {availableModules.map((module, index) => (
                    <div
                      key={module.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                        selectedModules.includes(module.id)
                          ? 'bg-emerald-500/20 border border-emerald-500/30'
                          : 'bg-white/5 border border-white/10 hover:bg-white/10'
                      }`}
                      onClick={() => toggleModule(module.id)}
                    >
                      <Checkbox
                        checked={selectedModules.includes(module.id)}
                        onCheckedChange={() => toggleModule(module.id)}
                      />
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">
                        {module.order_position + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{module.title}</p>
                        {module.description && (
                          <p className="text-white/50 text-xs truncate">{module.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Expected Completion Date */}
          {selectedModules.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <label className="text-white/60 text-sm mb-2 block">
                <Calendar className="w-4 h-4 inline mr-1" />
                Fecha esperada de completado (opcional)
              </label>
              <Input
                type="date"
                value={expectedCompletion}
                onChange={(e) => setExpectedCompletion(e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
                className="bg-white/5 border-white/10 text-white [&::-webkit-calendar-picker-indicator]:invert"
              />
            </motion.div>
          )}

          {/* Assign Button */}
          {selectedModules.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="pt-2"
            >
              <Button
                onClick={handleAssign}
                disabled={assignModules.isPending}
                className="w-full bg-emerald-500 hover:bg-emerald-600"
              >
                <Check className="w-4 h-4 mr-2" />
                Asignar {selectedModules.length} módulo{selectedModules.length !== 1 ? 's' : ''}
              </Button>
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
};
